import React from 'react';
import PropTypes from 'prop-types';
import { alpha, Box, Paper, Typography } from '@mui/material';

export const C = {
  emerald: '#059669', blue: '#2563eb', indigo: '#6366f1', violet: '#8b5cf6',
  amber: '#d97706', red: '#dc2626', slate: '#475569', ink: '#0f172a',
  paper: '#f8fafc', border: '#e2e8f0',
};

export const stageSkeletonSlots = ['slot-1', 'slot-2', 'slot-3', 'slot-4'];
export const tableSkeletonRows = ['row-1', 'row-2', 'row-3', 'row-4', 'row-5', 'row-6'];
export const tableSkeletonColumns = ['ticker', 'score', 'aiConf', 'delta', 'stage', 'tier', 'sector', 'price', 'dist', 'accum', 'rs20', 'rs60', 'strength'];

const tierColors = { A: '#059669', B: '#2563eb', C: '#f59e0b' };
const tierScores = { A: '>=80', B: '>=70', C: '>=60' };

export const sxPropType = PropTypes.oneOfType([
  PropTypes.object,
  PropTypes.array,
  PropTypes.func,
]);

export const localizedTextPropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    vi: PropTypes.string,
    en: PropTypes.string,
  }),
]);

export const absorptionDisplayPropType = PropTypes.shape({
  metrics: PropTypes.shape({
    absorption: PropTypes.shape({
      key: PropTypes.string,
      text: localizedTextPropType,
      tone: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }),
  insights: PropTypes.shape({
    absorption: PropTypes.shape({
      summary: localizedTextPropType,
      tone: PropTypes.string,
      evidence: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        text: localizedTextPropType,
        tone: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      })),
    }),
  }),
});

export const candidateRowPropType = PropTypes.shape({
  ticker: PropTypes.string,
  score_date: PropTypes.string,
  accumulation_stage: PropTypes.string,
  tier: PropTypes.string,
  company_name: PropTypes.string,
  market_regime: PropTypes.string,
  sector: PropTypes.string,
  sector_name: PropTypes.shape({
    vi: PropTypes.string,
    en: PropTypes.string,
  }),
  early_accumulation_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flags: PropTypes.shape({
    is_breakout_trigger: PropTypes.bool,
    is_early_accumulation: PropTypes.bool,
    is_pre_accumulation: PropTypes.bool,
    near_miss: PropTypes.bool,
  }),
  diagnostics: PropTypes.shape({
    criteria_passed: PropTypes.number,
    passed_checks: PropTypes.arrayOf(PropTypes.string),
    failed_checks: PropTypes.arrayOf(PropTypes.string),
  }),
  components: PropTypes.shape({
    market_sideways_score: PropTypes.number,
    relative_strength_score: PropTypes.number,
    base_compression_score: PropTypes.number,
    money_flow_score: PropTypes.number,
    trend_structure_score: PropTypes.number,
    risk_control_score: PropTypes.number,
  }),
  metrics: PropTypes.shape({
    accumulation_ratio: PropTypes.number,
    range20: PropTypes.number,
    drawdown20: PropTypes.number,
    base_readiness_lead: PropTypes.number,
    rs20: PropTypes.number,
    rs60: PropTypes.number,
  }),
  price: PropTypes.shape({
    close: PropTypes.number,
    support_20d: PropTypes.number,
    dist_from_resistance: PropTypes.number,
    base_support: PropTypes.number,
    base_resistance: PropTypes.number,
  }),
  liquidity: PropTypes.shape({
    avg_value_20d: PropTypes.number,
  }),
  strength: PropTypes.shape({
    strength_score: PropTypes.number,
  }),
  change: PropTypes.shape({
    delta: PropTypes.number,
    is_new: PropTypes.bool,
    prev_stage: PropTypes.string,
  }),
  // Backend-supplied AI confidence payload from M3 pattern reliability model.
  // Opaque on this row — the AIConfidenceBadge component owns its own shape.
  ai_confidence: PropTypes.shape({
    score: PropTypes.number,
    level: PropTypes.string,
    label: PropTypes.shape({
      code: PropTypes.string,
      vi: PropTypes.string,
      en: PropTypes.string,
    }),
    p_win_10d: PropTypes.number,
    p_win_20d: PropTypes.number,
    model_version: PropTypes.string,
  }),
  display: absorptionDisplayPropType,
});

export const thSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  color: '#64748b',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  py: 1,
  px: 1.5,
  borderBottom: `2px solid ${C.border}`,
  bgcolor: '#f8fafc',
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
};

export const tdSx = { py: 0.72, px: 1.5, fontSize: '0.77rem', borderBottom: '1px solid #f1f5f9' };

export const trSx = {
  '&:nth-of-type(even)': { bgcolor: '#fafbfc' },
  '&:hover': { bgcolor: alpha(C.blue, 0.05), '& td:first-of-type': { borderLeft: `2px solid ${C.blue}` } },
  transition: 'all 0.15s ease',
  cursor: 'pointer',
};

export const getLocale = (language) => (language === 'vi' ? 'vi-VN' : 'en-US');

export const fmtScore = (value, locale, digits = 2) => {
  if (value == null || Number.isNaN(Number(value))) return '-';
  return Number(value).toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export const fmtPct = (value, locale, scale = 100, digits = 2) => {
  if (value == null || Number.isNaN(Number(value))) return '-';
  const numeric = Number(value) * scale;
  return `${numeric >= 0 ? '+' : ''}${numeric.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
};

export const fmtInteger = (value, locale) => {
  if (value == null || Number.isNaN(Number(value))) return '-';
  return Number(value).toLocaleString(locale, { maximumFractionDigits: 0 });
};

export const localizedText = (value, lang) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const isVi = String(lang || '').toLowerCase().startsWith('vi');
  return (isVi ? value.vi : value.en) || value.en || value.vi || '';
};

export const toneColor = (tone) => {
  switch (tone) {
    case 'success':
      return C.emerald;
    case 'info':
      return C.blue;
    case 'warning':
      return C.amber;
    case 'danger':
      return C.red;
    default:
      return C.slate;
  }
};

export const metricToneFromGood = (good) => {
  if (good === true) return C.emerald;
  if (good === false) return C.amber;
  return '#475569';
};

export const resolveAccumulationStage = (row, fallback = 'NONE') => {
  if (row?.accumulation_stage) return row.accumulation_stage;
  if (row?.flags?.is_breakout_trigger) return 'BREAKOUT';
  if (row?.flags?.is_early_accumulation) return 'MATURE';
  if (row?.flags?.is_pre_accumulation) return 'FORMING';
  if (row?.flags?.near_miss) return 'NEAR_MISS';
  return fallback;
};

export const getFactorVisuals = (value, t) => {
  if (value >= 70) return { barColor: C.emerald, verdictWord: t('earlyAccV2.drawer.factorWord.good') };
  if (value >= 50) return { barColor: C.blue, verdictWord: t('earlyAccV2.drawer.factorWord.fair') };
  if (value >= 30) return { barColor: C.amber, verdictWord: t('earlyAccV2.drawer.factorWord.weak') };
  return { barColor: C.red, verdictWord: t('earlyAccV2.drawer.factorWord.bad') };
};

export const getCriteriaMeta = (criteria) => {
  if (criteria >= 7) return { tone: C.emerald, textKey: 'strong' };
  if (criteria >= 5) return { tone: C.blue, textKey: 'partial' };
  return { tone: C.amber, textKey: 'weak' };
};

export const getAccumulationSignalMeta = (accumRatio) => {
  if (accumRatio == null) return { tone: C.slate, textKey: 'unknown' };
  if (accumRatio >= 1.4) return { tone: C.emerald, textKey: 'strong' };
  if (accumRatio >= 1.1) return { tone: C.blue, textKey: 'present' };
  return { tone: C.amber, textKey: 'weak' };
};

export const getResistanceDistanceTone = (distance) => {
  if (distance == null) return C.slate;
  if (distance > 0) return C.emerald;
  if (distance >= -0.05) return C.blue;
  return C.amber;
};

export const getDrawdownTone = (drawdown) => {
  if (drawdown == null) return C.slate;
  if (drawdown >= -0.08) return C.emerald;
  return C.amber;
};

export const getDeltaColor = (delta) => {
  if (delta > 0.05) return C.emerald;
  if (delta < -0.05) return C.red;
  return '#94a3b8';
};

export const getHistoryPickTone = (pick) => {
  if (pick?.is_breakout) return C.emerald;
  if (pick?.is_early) return C.blue;
  if (pick?.near_miss) return C.amber;
  return C.slate;
};

export const reliabilityTone = (score) => {
  if (score >= 75) return C.emerald;
  if (score >= 68) return C.blue;
  if (score >= 60) return C.amber;
  return C.red;
};

export const getSkeletonWidth = (index) => {
  if (index === 0) return 38;
  if (index === 5) return 90;
  return 44;
};

export const hasValue = (value) => value != null;

export const formatIfPresent = (value, formatter, fallback = '-') => {
  if (!hasValue(value)) return fallback;
  return formatter(value);
};

export const metricGoodIfPresent = (value, predicate) => {
  if (!hasValue(value)) return false;
  return predicate(value);
};

export const absorptionMetricLabel = (text, value) => {
  if (!hasValue(value)) return text;
  return `${text} · ${value}`;
};

export const buildPipeline = (candidates) => {
  const buckets = { FORMING: [], MATURE: [], BREAKOUT: [], NEAR_MISS: [] };
  candidates.forEach((row) => {
    const stage = resolveAccumulationStage(row, 'NONE');
    if (buckets[stage]) buckets[stage].push(row);
  });
  Object.values(buckets).forEach((arr) => {
    arr.sort((a, b) => Number(b.early_accumulation_score) - Number(a.early_accumulation_score));
  });
  return buckets;
};

export const buildPromotions = (candidates) => {
  const order = { NONE: 0, NEW: 0, NEAR_MISS: 1, FORMING: 2, MATURE: 3, BREAKOUT: 4 };
  return candidates
    .map((row) => {
      const cur = row.accumulation_stage || 'NONE';
      const prev = row.change?.prev_stage || 'NEW';
      const upgraded = (order[cur] ?? 0) > (order[prev] ?? 0);
      return upgraded ? { row, from: prev, to: cur } : null;
    })
    .filter(Boolean)
    .sort((a, b) => (order[b.to] - order[a.to]) || (Number(b.row.early_accumulation_score) - Number(a.row.early_accumulation_score)));
};

export const sectorDisplay = (row, lang) => {
  const key = lang === 'vi' ? 'vi' : 'en';
  const localized = row?.sector_name?.[key] || row?.sector_name?.en;
  if (localized) return localized;
  return (row?.sector || '-').replace(/^L\d+_/, '');
};

export const formatStateLabel = (t, row) => {
  const stage = row?.accumulation_stage;
  if (stage === 'BREAKOUT' || row?.flags?.is_breakout_trigger) return t('dashboard.earlyAccumulation.states.breakout');
  if (stage === 'MATURE' || row?.flags?.is_early_accumulation) return t('dashboard.earlyAccumulation.states.qualified');
  if (stage === 'FORMING' || row?.flags?.is_pre_accumulation) return t('earlyAccV2.stateText.accumulating');
  if (row?.flags?.near_miss) return t('dashboard.earlyAccumulation.states.nearMiss');
  return t('dashboard.earlyAccumulation.states.watch');
};

export const formatCheckLabel = (t, key) => t(`dashboard.earlyAccumulation.checks.${key}`, key.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase()));
export const formatRegimeLabel = (value) => (value ? value.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase()) : '-');

export const describeMarketRegime = (value, lang) => {
  const code = String(value || '').toUpperCase();
  const vi = String(lang || '').toLowerCase().startsWith('vi');
  switch (code) {
    case 'SIDEWAY':
      return vi ? 'Thi truong di ngang' : 'Sideways market';
    case 'RECOVERY':
      return vi ? 'Thi truong hoi phuc' : 'Recovery market';
    case 'RISK_ON':
      return vi ? 'Thi truong thuan loi' : 'Risk-on market';
    case 'RISK_OFF':
      return vi ? 'Thi truong rui ro cao' : 'Risk-off market';
    default:
      return formatRegimeLabel(value);
  }
};

export const toneForFlag = (row) => {
  if (row?.flags?.is_breakout_trigger) return C.emerald;
  if (row?.flags?.is_early_accumulation) return C.blue;
  if (row?.flags?.is_pre_accumulation) return '#0ea5e9';
  if (row?.flags?.near_miss) return C.amber;
  return C.slate;
};

export const getTierMeta = (tier, t) => {
  if (tier && tierColors[tier]) {
    return {
      color: tierColors[tier],
      label: t(`earlyAccV2.tiers.${tier}.label`),
      score: tierScores[tier],
      desc: t(`earlyAccV2.tiers.${tier}.desc`),
    };
  }
  return { color: '#94a3b8', label: tier || '-', score: '-', desc: t('earlyAccV2.tiers.unknown') };
};

export function SectionCard({ children, sx = {} }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: 2, overflow: 'hidden', backgroundColor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', ...sx }}>
      {children}
    </Paper>
  );
}

SectionCard.propTypes = {
  children: PropTypes.node,
  sx: sxPropType,
};

export function SectionHeader({ title, description, action = null }) {
  return (
    <Box sx={{ px: 1.8, py: 1.2, borderBottom: '1px solid #f1f5f9', bgcolor: '#ffffff', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.2 }}>
      <Box>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#1e293b', letterSpacing: '0.01em' }}>{title}</Typography>
        {description ? <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.2 }}>{description}</Typography> : null}
      </Box>
      {action}
    </Box>
  );
}

SectionHeader.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  action: PropTypes.node,
};

export function MetricCell({ label, value, good, hint }) {
  const tone = metricToneFromGood(good);
  return (
    <Box sx={{ px: 0.65, py: 0.45, borderRadius: 0.6, bgcolor: alpha(tone, 0.05), border: `1px solid ${alpha(tone, 0.12)}`, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.2 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: tone, lineHeight: 1.2, mt: 0.1, fontFamily: 'inherit' }}>{value}</Typography>
      {hint ? <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', lineHeight: 1.2 }}>{hint}</Typography> : null}
    </Box>
  );
}

MetricCell.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  good: PropTypes.bool,
  hint: PropTypes.node,
};

export function FocusCard({ eyebrow, title, body, tone = C.slate, children }) {
  return (
    <Box sx={{ p: 1.2, borderRadius: 1.15, bgcolor: alpha(tone, 0.05), boxShadow: `inset 0 0 0 1px ${alpha(tone, 0.12)}` }}>
      {eyebrow ? <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: alpha(tone, 0.85), textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.35 }}>{eyebrow}</Typography> : null}
      {title ? <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>{title}</Typography> : null}
      {body ? <Typography sx={{ mt: 0.35, fontSize: '0.7rem', color: '#475569', lineHeight: 1.5 }}>{body}</Typography> : null}
      {children}
    </Box>
  );
}

FocusCard.propTypes = {
  eyebrow: PropTypes.node,
  title: PropTypes.node,
  body: PropTypes.node,
  tone: PropTypes.string,
  children: PropTypes.node,
};
